import {
  faDownload,
  faFileExcel,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/inputs/Button';
import Checkbox from 'components/inputs/Checkbox';
import TextButton from 'components/inputs/TextButton';
import SectionHeader from 'components/SectionHeader';
import TransparentSpinner from 'components/spinners/TransparentSpinner';
import Routes from 'constants/Routes';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import DateSpanPicker from '../../components/inputs/DateSpanPicker';
import DatePicker, { CustomInput } from 'components/inputs/DatePicker';
import LabelWrap from 'components/inputs/LabelWrap';
import { inputStyle } from 'components/inputs/Input';
import { adjustDateToNoon, formatDate } from 'utils/date-helpers';
import { AutorizedCheckbox } from 'pages/InternalDeliveryGroup/components';
import Roles from 'constants/Roles';
import useMe from 'contexts/authentication/useMe';
import {
  useExportExportApprovedAssignments,
  useExportExportAvailabilityData,
  useExportExportCircleKFuelReportHook,
  useExportExportHogiaSalaryHook,
  useExportExportHomeDeliveryDelayData,
  useExportExportInvoiceDataHook,
  useExportExportInvoiceStatsData,
  useExportExportPendingHook,
  useExportExportRawAssignmentData,
  useExportExportRestingTimesHook,
  useExportExportSalaryHook,
  useExportExportUserRatingData,
  useExportExportZoranInvoiceStatisticsHook,
} from 'api/export/export';
import expenseUtils from 'components/DetailedCase/Expenses/expenseUtils';
import { showException } from 'utils/exception-helper';
import { downloadBlob, downloadFile } from 'utils/file-utils';
import TooltipInfo from 'components/TooltipInfo';

const TooltipInfoDiv = styled.div`
  font-weight: normal;
  font-size: 0.9em;
  width: 400px;
`;

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 10px;
`;

const Horizontal = styled.div<{ align?: 'top' }>`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  margin: 10px 0px;
  gap: 10px;
`;

const Section = styled.section<{ compact?: boolean }>`
  width: 100%;
  max-width: 1000px;

  margin-bottom: ${(props) => (props.compact ? 20 : 100)}px;
`;

const SectionContent = styled.div`
  padding: 10px 0;
`;

const MyCheckbox = styled(Checkbox)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  padding: 6px;
`;

const MyAuthorizedCheckbox = styled(AutorizedCheckbox)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  padding: 6px;
`;

const MyButton = styled(Button)`
  ${inputStyle}
  background-color: ${(props) => props.theme.colors.background.button};
  color: ${(props) => props.theme.colors.foreground.button};
  height: 30px;
  border-style: none;
`;

const SubTitle = styled.h3`
  padding: 10px 0px;
  font-size: ${({ theme }) => theme.sizes.font.small};
`;

const HogiaSelections = styled.div`
  display: flex;
  gap: 10px;
`;

const ExcelExports: FC = () => {
  const navigate = useNavigate();
  const me = useMe();
  const hasEconomyRole = me?.roles.includes(Roles.Ekonomi);
  const isSuperAdmin = me?.roles.includes(Roles.SuperAdmin);

  const exportZoranInvoiceStatistics = useExportExportZoranInvoiceStatisticsHook();
  const exportZoranInvoiceStatsCall = useApiCallFn(
    (startDate: Date, endDate: Date, includeNotYetClosed: boolean) =>
      exportZoranInvoiceStatistics({
        StartDate: startDate,
        EndDate: endDate,
        IncludeNotYetClosed: includeNotYetClosed,
      })
  );

  const exportInvoiceStatsDataCall = useExportExportInvoiceStatsData();

  // const exportInvoiceStatsV2Call = useApiCall(
  //   ExportClient,
  //   (c, startDate: Date, endDate: Date, includeNotYetClosed: boolean) =>
  //     c.exportInvoiceStatisticsV2(startDate, endDate, includeNotYetClosed)
  // );

  const exportInvoiceData = useExportExportInvoiceDataHook();
  const exportInvoiceDataCall = useApiCallFn(
    (endDate: Date, markAsInvoiced: boolean, filterOnClosedDate: boolean) =>
      exportInvoiceData({
        BeforeDate: endDate,
        MarkAsInvoiced: markAsInvoiced,
        filterOnClosedDate,
      })
  );

  const exportAvailabilityDataCall = useExportExportAvailabilityData();
  const exportUserRatingDataCall = useExportExportUserRatingData();
  const exportHomeDeliveryDelayDataCall =
    useExportExportHomeDeliveryDelayData();
  const exportApprovedAssignmentsCall = useExportExportApprovedAssignments();
  const exportRawAssignmentDataCall = useExportExportRawAssignmentData();

  const exportRestingTimes = useExportExportRestingTimesHook();
  const exportRestingTimesCall = useApiCallFn(
    (startDate: Date, endDate: Date) =>
      exportRestingTimes({
        StartDate: startDate,
        EndDate: endDate,
        IncludeZeroDays: true,
      })
  );

  const [markCasesAsInvoiced, setMarkCasesAsInvoiced] =
    useState<boolean>(false);

  const [includeNotYetClosed, setIncludeNotYetClosed] =
    useState<boolean>(false);

  const [filterStatsOnCreatedDate, setFilterStatsOnCreatedDate] =
    useState<boolean>(false);

  const [filterInvoiceOnCreatedDate, setFilterInvoiceOnCreatedDate] =
    useState<boolean>(false);

  const exportPending = useExportExportPendingHook();
  const exportPendingCall = useApiCallFn(() => exportPending());

  const exportSalary = useExportExportSalaryHook();
  const exportSalaryCall = useApiCallFn((startDate: Date, endDate: Date) =>
    exportSalary({ StartDate: startDate, EndDate: endDate })
  );

  const exportHogiaSalary = useExportExportHogiaSalaryHook();
  const exportHogiaSalaryCall = useApiCallFn((startDate: Date, endDate: Date) =>
    exportHogiaSalary({ StartDate: startDate, EndDate: endDate })
  );

  const exportCircleKFuelReport = useExportExportCircleKFuelReportHook();
  const exportCircleKFuelReportCall = useApiCallFn(
    (startDate: Date, endDate: Date) =>
      exportCircleKFuelReport({ StartDate: startDate, EndDate: endDate })
  );

  const now = new Date();
  const lastMonthStart =
    adjustDateToNoon(new Date(now.getFullYear(), now.getMonth() - 1, 1)) ??
    undefined;
  const lastMonthEnd = adjustDateToNoon(
    new Date(now.getFullYear(), now.getMonth(), 0)
  ) as Date;

  const [invoiceDate, setInvoiceDate] = useState<Date>(now);

  const getPendingClick = async () => {
    const [result, error] = await exportPendingCall.run();

    if (result && !error) {
      downloadBlob(result, 'Väntande.xlsx');
    }
  };

  const handleDownloadInvoice = async () => {
    const [result, error] = await exportInvoiceDataCall.run(
      invoiceDate,
      markCasesAsInvoiced,
      !filterInvoiceOnCreatedDate
    );
    if (result && !error) {
      downloadBlob(result, `Fakturaunderlag_${formatDate(invoiceDate)}.xlsx`);
    }
  };

  return (
    <Wrapper>
      <Section>
        <SectionHeader>Löneunderlag</SectionHeader>
        <SectionContent>
          <DateSpanPicker
            initialFrom={lastMonthStart}
            initialTo={lastMonthEnd}
            loading={exportSalaryCall.status === RequestStatus.Fetching}
            okButtonLabel={
              <>
                <FontAwesomeIcon icon={faDownload} /> Hämta löneunderlag
              </>
            }
            onDatesSelected={async ({ fromDate, toDate }) => {
              const [result, error] = await exportSalaryCall.run(
                fromDate,
                toDate
              );

              if (result && !error) {
                downloadBlob(
                  result,
                  `Lön_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                );
              }
            }}
            populateHolidays
          />
        </SectionContent>
        {hasEconomyRole && (
          <SectionContent>
            <SubTitle>Export till Hogia</SubTitle>
            <HogiaSelections>
              <DateSpanPicker
                initialFrom={lastMonthStart}
                initialTo={lastMonthEnd}
                loading={
                  exportHogiaSalaryCall.status === RequestStatus.Fetching
                }
                okButtonLabel={
                  <>
                    <FontAwesomeIcon icon={faDownload} /> Hämta Hogia-export
                  </>
                }
                onDatesSelected={async ({ fromDate, toDate }) => {
                  const [result, error] = await exportHogiaSalaryCall.run(
                    fromDate,
                    toDate
                  );
                  if (result && !error) {
                    downloadFile(
                      result,
                      `HogiaLön_${formatDate(fromDate)}_${formatDate(toDate)}.xml`
                    );
                  }
                }}
                populateHolidays
                hasDropdowns
              />
            </HogiaSelections>
          </SectionContent>
        )}
      </Section>

      <Section>
        <SectionHeader>
          Faktureringsunderlag{' '}
          <TooltipInfo
            info={
              <TooltipInfoDiv>
                Faktureringsunderlaget innehåller stängda och ofakturerade
                ärenden inom vald tidsperiod.
              </TooltipInfoDiv>
            }
          />
        </SectionHeader>

        <SectionContent
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Horizontal>
            <LabelWrap label="Till">
              <DatePicker
                calendarStartDay={1}
                customInput={<CustomInput date={invoiceDate} />}
                dateFormat="yyyy-MM-dd"
                disabled={
                  exportInvoiceDataCall.status === RequestStatus.Fetching
                }
                locale="sv"
                onChange={(date) => {
                  if (date) {
                    setInvoiceDate(date);
                  }
                }}
                populateHolidays
                selected={invoiceDate}
              />
            </LabelWrap>
            <MyButton
              disabled={exportInvoiceDataCall.status === RequestStatus.Fetching}
              onClick={handleDownloadInvoice}
            >
              {exportInvoiceDataCall.status === RequestStatus.Fetching ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faDownload} />
              )}
              <span> Hämta underlag</span>
            </MyButton>
            <MyAuthorizedCheckbox
              checked={markCasesAsInvoiced}
              onChange={(event) =>
                setMarkCasesAsInvoiced(event.currentTarget.checked)
              }
              roles={[Roles.Ekonomi]}
            >
              Markera som fakturerat
            </MyAuthorizedCheckbox>

            <MyCheckbox
              checked={filterInvoiceOnCreatedDate}
              onChange={(event) =>
                setFilterInvoiceOnCreatedDate(event.currentTarget.checked)
              }
            >
              Filtrera på Skapat datum istället för Slutförtdatum
            </MyCheckbox>
          </Horizontal>
        </SectionContent>
        <SectionContent>
          {exportInvoiceDataCall.status === RequestStatus.Fetching && (
            <span>
              Hämtar underlag. Det kan upp till ett par minuter beroende på hur
              mycket data som behöver gås igenom.
            </span>
          )}
        </SectionContent>
        <SectionContent>
          <TextButton
            onClick={() => {
              navigate(Routes.excelExports.history);
            }}
          >
            <FontAwesomeIcon icon={faFileExcel} /> Visa exporthistorik
          </TextButton>
        </SectionContent>
      </Section>

      <Section>
        <SectionHeader>
          Statistikunderlag{' '}
          <TooltipInfo
            info={
              <TooltipInfoDiv>
                Statistikunderlaget innehåller samtliga stängda / skapade
                ärenden inom en vald tidsperiod.
              </TooltipInfoDiv>
            }
          />
        </SectionHeader>

        <SectionContent>
          <Horizontal>
            <DateSpanPicker
              initialFrom={lastMonthStart}
              initialTo={lastMonthEnd}
              loading={exportInvoiceStatsDataCall.status === 'loading'}
              okButtonLabel={
                <>
                  <FontAwesomeIcon icon={faDownload} /> Hämta underlag
                </>
              }
              onDatesSelected={async ({ fromDate, toDate }) => {
                try {
                  const result = await exportInvoiceStatsDataCall.mutateAsync({
                    params: {
                      BeforeDate: toDate,
                      AfterDate: fromDate,
                      includeInvoiced: true,
                      filterOnClosedDate: !filterStatsOnCreatedDate,
                    },
                  });
                  if (result) {
                    downloadBlob(
                      result,
                      `Statistikunderlag_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                    );
                  }
                } catch (error) {
                  showException(error);
                }
              }}
              populateHolidays
            />
            <MyCheckbox
              checked={filterStatsOnCreatedDate}
              onChange={(event) =>
                setFilterStatsOnCreatedDate(event.currentTarget.checked)
              }
            >
              Filtrera på Skapat datum istället för Slutförtdatum
            </MyCheckbox>
          </Horizontal>
        </SectionContent>
      </Section>

      <Section>
        <SectionHeader>
          Vilotidsunderlag{' '}
          <TooltipInfo
            info={
              <TooltipInfoDiv>
                Vilotidsunderlaget innehåller information om Fälttestares
                vilotider
              </TooltipInfoDiv>
            }
          />
        </SectionHeader>

        <Horizontal>
          <DateSpanPicker
            initialFrom={lastMonthStart}
            initialTo={lastMonthEnd}
            loading={exportRestingTimesCall.status === RequestStatus.Fetching}
            okButtonLabel={
              <>
                <FontAwesomeIcon icon={faDownload} /> Hämta underlag
              </>
            }
            onDatesSelected={async ({ fromDate, toDate }) => {
              const [result, error] = await exportRestingTimesCall.run(
                fromDate,
                toDate
              );

              if (result && !error) {
                downloadBlob(
                  result,
                  `Vilotider_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                );
              }
            }}
            populateHolidays
          />
        </Horizontal>
      </Section>

      <Section>
        <SectionHeader>
          Tillgänglighetsunderlag{' '}
          <TooltipInfo
            info={
              <TooltipInfoDiv>
                Tillgänglighetsunderlaget innehåller information om Fälttestares
                tillgänglighet och ledighetsförfrågningar
              </TooltipInfoDiv>
            }
          />
        </SectionHeader>

        <Horizontal>
          <DateSpanPicker
            initialFrom={lastMonthStart}
            initialTo={lastMonthEnd}
            loading={exportRestingTimesCall.status === RequestStatus.Fetching}
            okButtonLabel={
              <>
                <FontAwesomeIcon icon={faDownload} /> Hämta underlag
              </>
            }
            onDatesSelected={async ({ fromDate, toDate }) => {
              try {
                const result = await exportAvailabilityDataCall.mutateAsync({
                  params: {
                    StartDate: fromDate,
                    EndDate: toDate,
                  },
                });
                if (result) {
                  downloadBlob(result, 'Tillgänglighetsunderlag.xlsx');
                }
              } catch (error) {
                showException(error);
              }
            }}
            populateHolidays
          />
        </Horizontal>
      </Section>

      <Section>
        <SectionHeader>
          Utvärderingsunderlag{' '}
          <TooltipInfo
            info={
              <TooltipInfoDiv>
                Utvärderingsunderlaget innehåller information om slutkundernas
                utvärderingar av Ärendet och Fälttestaren
              </TooltipInfoDiv>
            }
          />
        </SectionHeader>

        <Horizontal>
          <DateSpanPicker
            initialFrom={lastMonthStart}
            initialTo={lastMonthEnd}
            loading={exportRestingTimesCall.status === RequestStatus.Fetching}
            okButtonLabel={
              <>
                <FontAwesomeIcon icon={faDownload} /> Hämta underlag
              </>
            }
            onDatesSelected={async ({ fromDate, toDate }) => {
              try {
                const result = await exportUserRatingDataCall.mutateAsync({
                  params: {
                    StartDate: fromDate,
                    EndDate: toDate,
                  },
                });
                if (result) {
                  downloadBlob(
                    result,
                    `Utvärderingsunderlag_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                  );
                }
              } catch (error) {
                showException(error);
              }
            }}
            populateHolidays
          />
        </Horizontal>
      </Section>

      <Section>
        <SectionHeader>
          Hemleverans Förseningsanalys{' '}
          <TooltipInfo
            info={
              <TooltipInfoDiv>
                Hemleverans förseningsanalys innehåller data om förseningar i
                HomeDelivery-ärenden med tidsstämplar för olika milstolpar och
                deltaberäkningar i minuter.
              </TooltipInfoDiv>
            }
          />
        </SectionHeader>

        <Horizontal>
          <DateSpanPicker
            initialFrom={lastMonthStart}
            initialTo={lastMonthEnd}
            loading={exportHomeDeliveryDelayDataCall.status === 'loading'}
            okButtonLabel={
              <>
                <FontAwesomeIcon icon={faDownload} /> Hämta förseningsanalys
              </>
            }
            onDatesSelected={async ({ fromDate, toDate }) => {
              try {
                const result =
                  await exportHomeDeliveryDelayDataCall.mutateAsync({
                    params: {
                      StartDate: fromDate,
                      EndDate: toDate,
                    },
                  });
                if (result) {
                  downloadBlob(
                    result,
                    `Hemleverans_förseningar_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                  );
                }
              } catch (error) {
                showException(error);
              }
            }}
            populateHolidays
          />
        </Horizontal>
      </Section>

      <Section>
        <SectionHeader>
          Godkända Underlag{' '}
          <TooltipInfo
            info={
              <TooltipInfoDiv>
                Godkända underlag innehåller information om godkända uppdrag
                inom en vald tidsperiod, inklusive godkännandedatum,
                registreringsnummer, start- och sluttider samt varaktighet.
              </TooltipInfoDiv>
            }
          />
        </SectionHeader>

        <Horizontal>
          <DateSpanPicker
            initialFrom={lastMonthStart}
            initialTo={lastMonthEnd}
            loading={exportApprovedAssignmentsCall.status === 'loading'}
            okButtonLabel={
              <>
                <FontAwesomeIcon icon={faDownload} /> Hämta underlag
              </>
            }
            onDatesSelected={async ({ fromDate, toDate }) => {
              try {
                const result = await exportApprovedAssignmentsCall.mutateAsync({
                  params: {
                    StartDate: fromDate,
                    EndDate: toDate,
                  },
                });
                if (result) {
                  downloadBlob(
                    result,
                    `Godkända_underlag_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                  );
                }
              } catch (error) {
                showException(error);
              }
            }}
            populateHolidays
          />
        </Horizontal>
      </Section>

      {expenseUtils.allowCircleKSensitiveInfo(me) && (
        <Section>
          <SectionHeader>
            Circle K Utgiftsrapport{' '}
            <TooltipInfo
              info={
                <TooltipInfoDiv>
                  Utgiftsrapport med data från Circle K-kvitton (diesel, bensin,
                  AdBlue, olja, biltvätt, parkering) grupperat per kostnadsställe
                  (Hogia-klassificering) med status (avbok/sump), antal och kostnad.
                </TooltipInfoDiv>
              }
            />
          </SectionHeader>

          <Horizontal>
            <DateSpanPicker
              initialFrom={lastMonthStart}
              initialTo={lastMonthEnd}
              loading={
                exportCircleKFuelReportCall.status === RequestStatus.Fetching
              }
              okButtonLabel={
                <>
                  <FontAwesomeIcon icon={faDownload} /> Hämta utgiftsrapport
                </>
              }
              onDatesSelected={async ({ fromDate, toDate }) => {
                const [result, error] = await exportCircleKFuelReportCall.run(
                  fromDate,
                  toDate
                );

                if (result && !error) {
                  downloadBlob(
                    result,
                    `CircleK_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                  );
                }
              }}
              populateHolidays
            />
          </Horizontal>
        </Section>
      )}

      {isSuperAdmin && (
        <Section>
          <SectionHeader>
            Rådataexport{' '}
            <TooltipInfo
              info={
                <TooltipInfoDiv>
                  Rådataexport innehåller samtliga uppdrag med tillhörande
                  ärende-information, tilldelad person, adresser och tider för
                  en vald tidsperiod.
                </TooltipInfoDiv>
              }
            />
          </SectionHeader>

          <Horizontal>
            <DateSpanPicker
              initialFrom={lastMonthStart}
              initialTo={lastMonthEnd}
              loading={exportRawAssignmentDataCall.status === 'loading'}
              okButtonLabel={
                <>
                  <FontAwesomeIcon icon={faDownload} /> Hämta rådataexport
                </>
              }
              onDatesSelected={async ({ fromDate, toDate }) => {
                try {
                  const result =
                    await exportRawAssignmentDataCall.mutateAsync({
                      params: {
                        StartDate: fromDate,
                        EndDate: toDate,
                      },
                    });
                  if (result) {
                    downloadBlob(
                      result,
                      `Rådataexport_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                    );
                  }
                } catch (error) {
                  showException(error);
                }
              }}
              populateHolidays
            />
          </Horizontal>
        </Section>
      )}

      <Section>
        <SectionHeader>Inventarie</SectionHeader>

        <SectionContent>
          <Button
            disabled={exportPendingCall.status === RequestStatus.Fetching}
            onClick={getPendingClick}
            style={{ width: 220 }}
          >
            {exportPendingCall.status === RequestStatus.Fetching ? (
              <TransparentSpinner currentColor small />
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} /> Hämta inventarie
              </>
            )}
          </Button>
        </SectionContent>
      </Section>

      <Section>
        <SectionHeader>Zorans Statistikunderlag</SectionHeader>

        <SectionContent>
          <Horizontal>
            <DateSpanPicker
              initialFrom={lastMonthStart}
              initialTo={lastMonthEnd}
              loading={
                exportZoranInvoiceStatsCall.status === RequestStatus.Fetching
              }
              okButtonLabel={
                <>
                  <FontAwesomeIcon icon={faDownload} /> Hämta underlag
                </>
              }
              onDatesSelected={async ({ fromDate, toDate }) => {
                const [result, error] = await exportZoranInvoiceStatsCall.run(
                  fromDate,
                  toDate,
                  includeNotYetClosed
                );

                if (result && !error) {
                  downloadBlob(
                    result,
                    `Statistik_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`
                  );
                }
              }}
              populateHolidays
            />
            <MyCheckbox
              checked={includeNotYetClosed}
              onChange={(event) =>
                setIncludeNotYetClosed(event.currentTarget.checked)
              }
            >
              Inkludera ännu ej stängda ärenden
            </MyCheckbox>
          </Horizontal>
        </SectionContent>
      </Section>
    </Wrapper>
  );
};

export default ExcelExports;
